import { useT } from '../context/useT';
import type { PreviewDictionary } from '../i18n/types';
import type { PreviewTabKey } from '../theme/tokens';
import { usePreviewTheme } from '../theme/ThemeProvider';
import style from './style.module.scss';

type IconProps = { filled?: boolean };

const HomeIcon = ({ filled }: IconProps) =>
    filled ? (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M21 10.8328V19.5C21 19.8978 20.842 20.2794 20.5607 20.5607C20.2794 20.842 19.8978 21 19.5 21H15.75C15.3522 21 14.9706 20.842 14.6893 20.5607C14.408 20.2794 14.25 19.8978 14.25 19.5V15.75C14.25 15.5511 14.171 15.3603 14.0303 15.2197C13.8897 15.079 13.6989 15 13.5 15H10.5C10.3011 15 10.1103 15.079 9.96967 15.2197C9.82902 15.3603 9.75 15.5511 9.75 15.75V19.5C9.75 19.8978 9.59196 20.2794 9.31066 20.5607C9.02936 20.842 8.64782 21 8.25 21H4.5C4.10218 21 3.72064 20.842 3.43934 20.5607C3.15804 20.2794 3 19.8978 3 19.5V10.8328C2.99997 10.6252 3.04303 10.4199 3.12646 10.2298C3.20989 10.0397 3.33187 9.86896 3.48469 9.72844L10.9847 2.65219L10.995 2.64188C11.2711 2.39075 11.631 2.25159 12.0042 2.25159C12.3775 2.25159 12.7373 2.39075 13.0134 2.64188C13.0166 2.64554 13.0201 2.64899 13.0238 2.65219L20.5238 9.72844C20.675 9.8697 20.7954 10.0407 20.8774 10.2308C20.9594 10.4209 21.0011 10.6258 21 10.8328Z"
                fill="currentColor"
            />
        </svg>
    ) : (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M20.5153 9.72844L13.0153 2.65219C13.0116 2.64899 13.0082 2.64554 13.005 2.64188C12.7289 2.39075 12.369 2.25159 11.9958 2.25159C11.6225 2.25159 11.2627 2.39075 10.9866 2.64188L10.9763 2.65219L3.48469 9.72844C3.33187 9.86896 3.20989 10.0397 3.12646 10.2298C3.04303 10.4199 2.99997 10.6252 3 10.8328V19.5C3 19.8978 3.15804 20.2794 3.43934 20.5607C3.72064 20.842 4.10218 21 4.5 21H9C9.39782 21 9.77936 20.842 10.0607 20.5607C10.342 20.2794 10.5 19.8978 10.5 19.5V15H13.5V19.5C13.5 19.8978 13.658 20.2794 13.9393 20.5607C14.2206 20.842 14.6022 21 15 21H19.5C19.8978 21 20.2794 20.842 20.5607 20.5607C20.842 20.2794 21 19.8978 21 19.5V10.8328C21 10.6252 20.957 10.4199 20.8735 10.2298C20.7901 10.0397 20.6681 9.86896 20.5153 9.72844ZM19.5 19.5H15V15C15 14.6022 14.842 14.2206 14.5607 13.9393C14.2794 13.658 13.8978 13.5 13.5 13.5H10.5C10.1022 13.5 9.72064 13.658 9.43934 13.9393C9.15804 14.2206 9 14.6022 9 15V19.5H4.5V10.8328L4.51031 10.8234L12 3.75L19.4906 10.8216L19.5009 10.8309L19.5 19.5Z"
                fill="currentColor"
            />
        </svg>
    );

const MapPinIcon = ({ filled }: IconProps) =>
    filled ? (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M12 1.5C9.81273 1.50248 7.71575 2.37247 6.16911 3.91911C4.62247 5.46575 3.75248 7.56273 3.75 9.75C3.75 16.8094 11.25 22.1409 11.5697 22.3641C11.6958 22.4524 11.846 22.4998 12 22.4998C12.154 22.4998 12.3042 22.4524 12.4303 22.3641C12.75 22.1409 20.25 16.8094 20.25 9.75C20.2475 7.56273 19.3775 5.46575 17.8309 3.91911C16.2843 2.37247 14.1873 1.50248 12 1.5ZM12 6.75C12.5933 6.75 13.1734 6.92595 13.6667 7.25559C14.1601 7.58524 14.5446 8.05377 14.7716 8.60195C14.9987 9.15013 15.0581 9.75333 14.9424 10.3353C14.8266 10.9172 14.5409 11.4518 14.1213 11.8713C13.7018 12.2909 13.1672 12.5766 12.5853 12.6924C12.0033 12.8081 11.4001 12.7487 10.8519 12.5216C10.3038 12.2946 9.83524 11.9101 9.50559 11.4167C9.17595 10.9234 9 10.3433 9 9.75C9 8.95435 9.31607 8.19129 9.87868 7.62868C10.4413 7.06607 11.2044 6.75 12 6.75Z"
                fill="currentColor"
            />
        </svg>
    ) : (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M12 6C11.2583 6 10.5333 6.21993 9.91661 6.63199C9.29993 7.04404 8.81928 7.62971 8.53545 8.31494C8.25162 9.00016 8.17736 9.75416 8.32205 10.4816C8.46675 11.209 8.8239 11.8772 9.34835 12.4017C9.8728 12.9261 10.541 13.2833 11.2684 13.4279C11.9958 13.5726 12.7498 13.4984 13.4351 13.2145C14.1203 12.9307 14.706 12.4501 15.118 11.8334C15.5301 11.2167 15.75 10.4917 15.75 9.75C15.75 8.75544 15.3549 7.80161 14.6517 7.09835C13.9484 6.39509 12.9946 6 12 6ZM12 12C11.555 12 11.12 11.868 10.75 11.6208C10.38 11.3736 10.0916 11.0222 9.92127 10.611C9.75097 10.1999 9.70642 9.7475 9.79323 9.31105C9.88005 8.87459 10.0943 8.47368 10.409 8.15901C10.7237 7.84434 11.1246 7.63005 11.561 7.54323C11.9975 7.45642 12.4499 7.50097 12.861 7.67127C13.2722 7.84157 13.6236 8.12996 13.8708 8.49997C14.118 8.86998 14.25 9.30499 14.25 9.75C14.25 10.3467 14.0129 10.919 13.591 11.341C13.169 11.7629 12.5967 12 12 12ZM12 1.5C9.81273 1.50248 7.71575 2.37247 6.16911 3.91911C4.62247 5.46575 3.75248 7.56273 3.75 9.75C3.75 12.6938 5.11031 15.8138 7.6875 18.7734C8.84552 20.1108 10.1489 21.3151 11.5734 22.3641C11.6995 22.4524 11.8498 22.4998 12.0037 22.4998C12.1577 22.4998 12.308 22.4524 12.4341 22.3641C13.856 21.3147 15.1568 20.1104 16.3125 18.7734C18.8859 15.8138 20.25 12.6938 20.25 9.75C20.2475 7.56273 19.3775 5.46575 17.8309 3.91911C16.2843 2.37247 14.1873 1.50248 12 1.5ZM12 20.8125C10.4503 19.5938 5.25 15.1172 5.25 9.75C5.25 7.95979 5.96116 6.2429 7.22703 4.97703C8.4929 3.71116 10.2098 3 12 3C13.7902 3 15.5071 3.71116 16.773 4.97703C18.0388 6.2429 18.75 7.95979 18.75 9.75C18.75 15.1153 13.5497 19.5938 12 20.8125Z"
                fill="currentColor"
            />
        </svg>
    );

const TicketIcon = ({ filled }: IconProps) =>
    filled ? (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M21.2991 9.795C21.6379 9.72606 21.9425 9.54219 22.1613 9.27449C22.3802 9.00679 22.4998 8.6717 22.5 8.32594V6C22.5 5.60218 22.342 5.22064 22.0607 4.93934C21.7794 4.65804 21.3978 4.5 21 4.5H3C2.60218 4.5 2.22065 4.65804 1.93934 4.93934C1.65804 5.22064 1.5 5.60218 1.5 6V8.32594C1.50019 8.6717 1.61983 9.00679 1.83867 9.27449C2.05751 9.54219 2.36212 9.72606 2.70094 9.795C3.20757 9.90006 3.66251 10.1765 3.98912 10.5778C4.31573 10.9791 4.49406 11.4807 4.49406 11.9981C4.49406 12.5155 4.31573 13.0171 3.98912 13.4184C3.66251 13.8197 3.20757 14.0962 2.70094 14.2013C2.36148 14.2703 2.0564 14.4547 1.83748 14.7232C1.61857 14.9917 1.49933 15.3277 1.5 15.6741V18C1.5 18.3978 1.65804 18.7794 1.93934 19.0607C2.22065 19.342 2.60218 19.5 3 19.5H21C21.3978 19.5 21.7794 19.342 22.0607 19.0607C22.342 18.7794 22.5 18.3978 22.5 18V15.6741C22.4998 15.3283 22.3802 14.9932 22.1613 14.7255C21.9425 14.4578 21.6379 14.2739 21.2991 14.205C20.7924 14.0999 20.3375 13.8235 20.0109 13.4222C19.6843 13.0209 19.5059 12.5193 19.5059 12.0019C19.5059 11.4845 19.6843 10.9829 20.0109 10.5816C20.3375 10.1803 20.7924 9.90381 21.2991 9.79875V9.795ZM3 15.675C3.84722 15.5024 4.60876 15.0424 5.15573 14.3728C5.70269 13.7031 6.00146 12.8651 6.00146 12.0005C6.00146 11.1359 5.70269 10.2978 5.15573 9.62819C4.60876 8.95857 3.84722 8.49852 3 8.32594V6H8.25V18H3V15.675Z"
                fill="currentColor"
            />
        </svg>
    ) : (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M21.2991 9.795C21.6379 9.72606 21.9425 9.54219 22.1613 9.27449C22.3802 9.00679 22.4998 8.6717 22.5 8.32594V6C22.5 5.60218 22.342 5.22064 22.0607 4.93934C21.7794 4.65804 21.3978 4.5 21 4.5H3C2.60218 4.5 2.22065 4.65804 1.93934 4.93934C1.65804 5.22064 1.5 5.60218 1.5 6V8.32594C1.50019 8.6717 1.61983 9.00679 1.83867 9.27449C2.05751 9.54219 2.36212 9.72606 2.70094 9.795C3.20757 9.90006 3.66251 10.1765 3.98912 10.5778C4.31573 10.9791 4.49406 11.4807 4.49406 11.9981C4.49406 12.5155 4.31573 13.0171 3.98912 13.4184C3.66251 13.8197 3.20757 14.0962 2.70094 14.2013C2.36148 14.2703 2.0564 14.4547 1.83748 14.7232C1.61857 14.9917 1.49933 15.3277 1.5 15.6741V18C1.5 18.3978 1.65804 18.7794 1.93934 19.0607C2.22065 19.342 2.60218 19.5 3 19.5H21C21.3978 19.5 21.7794 19.342 22.0607 19.0607C22.342 18.7794 22.5 18.3978 22.5 18V15.6741C22.4998 15.3283 22.3802 14.9932 22.1613 14.7255C21.9425 14.4578 21.6379 14.2739 21.2991 14.205C20.7924 14.0999 20.3375 13.8235 20.0109 13.4222C19.6843 13.0209 19.5059 12.5193 19.5059 12.0019C19.5059 11.4845 19.6843 10.9829 20.0109 10.5816C20.3375 10.1803 20.7924 9.90381 21.2991 9.79875V9.795ZM3 15.675C3.84722 15.5024 4.60876 15.0424 5.15573 14.3728C5.70269 13.7031 6.00146 12.8651 6.00146 12.0005C6.00146 11.1359 5.70269 10.2978 5.15573 9.62819C4.60876 8.95857 3.84722 8.49852 3 8.32594V6H8.25V18H3V15.675ZM21 15.675V18H9.75V6H21V8.325C20.1528 8.49759 19.3912 8.95763 18.8443 9.62725C18.2973 10.2969 17.9985 11.1349 17.9985 11.9995C17.9985 12.8641 18.2973 13.7022 18.8443 14.3718C19.3912 15.0414 20.1528 15.5015 21 15.6741V15.675Z"
                fill="currentColor"
            />
        </svg>
    );

const CarIcon = ({ filled }: IconProps) =>
    filled ? (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M22.5 10.5H21.4875L18.8831 4.64062C18.7653 4.37553 18.5731 4.15029 18.3298 3.99222C18.0865 3.83414 17.8026 3.75 17.5125 3.75H6.4875C6.19738 3.75 5.91349 3.83414 5.67022 3.99222C5.42695 4.15029 5.23474 4.37553 5.11688 4.64062L2.5125 10.5H1.5C1.30109 10.5 1.11032 10.579 0.96967 10.7197C0.829018 10.8603 0.75 11.0511 0.75 11.25C0.75 11.4489 0.829018 11.6397 0.96967 11.7803C1.11032 11.921 1.30109 12 1.5 12H2.25V19.5C2.25 19.8978 2.40804 20.2794 2.68934 20.5607C2.97064 20.842 3.35218 21 3.75 21H6C6.39782 21 6.77936 20.842 7.06066 20.5607C7.34196 20.2794 7.5 19.8978 7.5 19.5V18H16.5V19.5C16.5 19.8978 16.658 20.2794 16.9393 20.5607C17.2206 20.842 17.6022 21 18 21H20.25C20.6478 21 21.0294 20.842 21.3107 20.5607C21.592 20.2794 21.75 19.8978 21.75 19.5V12H22.5C22.6989 12 22.8897 11.921 23.0303 11.7803C23.171 11.6397 23.25 11.4489 23.25 11.25C23.25 11.0511 23.171 10.8603 23.0303 10.7197C22.8897 10.579 22.6989 10.5 22.5 10.5ZM7.5 15H6C5.80109 15 5.61032 14.921 5.46967 14.7803C5.32902 14.6397 5.25 14.4489 5.25 14.25C5.25 14.0511 5.32902 13.8603 5.46967 13.7197C5.61032 13.579 5.80109 13.5 6 13.5H7.5C7.69891 13.5 7.88968 13.579 8.03033 13.7197C8.17098 13.8603 8.25 14.0511 8.25 14.25C8.25 14.4489 8.17098 14.6397 8.03033 14.7803C7.88968 14.921 7.69891 15 7.5 15ZM16.5 15C16.3011 15 16.1103 14.921 15.9697 14.7803C15.829 14.6397 15.75 14.4489 15.75 14.25C15.75 14.0511 15.829 13.8603 15.9697 13.7197C16.1103 13.579 16.3011 13.5 16.5 13.5H18C18.1989 13.5 18.3897 13.579 18.5303 13.7197C18.671 13.8603 18.75 14.0511 18.75 14.25C18.75 14.4489 18.671 14.6397 18.5303 14.7803C18.3897 14.921 18.1989 15 18 15H16.5Z"
                fill="currentColor"
            />
        </svg>
    ) : (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M22.5 10.5H21.4875L18.8831 4.64062C18.7653 4.37553 18.5731 4.15029 18.3298 3.99222C18.0865 3.83414 17.8026 3.75 17.5125 3.75H6.4875C6.19738 3.75 5.91349 3.83414 5.67022 3.99222C5.42695 4.15029 5.23474 4.37553 5.11688 4.64062L2.5125 10.5H1.5C1.30109 10.5 1.11032 10.579 0.96967 10.7197C0.829018 10.8603 0.75 11.0511 0.75 11.25C0.75 11.4489 0.829018 11.6397 0.96967 11.7803C1.11032 11.921 1.30109 12 1.5 12H2.25V19.5C2.25 19.8978 2.40804 20.2794 2.68934 20.5607C2.97064 20.842 3.35218 21 3.75 21H6C6.39782 21 6.77936 20.842 7.06066 20.5607C7.34196 20.2794 7.5 19.8978 7.5 19.5V18H16.5V19.5C16.5 19.8978 16.658 20.2794 16.9393 20.5607C17.2206 20.842 17.6022 21 18 21H20.25C20.6478 21 21.0294 20.842 21.3107 20.5607C21.592 20.2794 21.75 19.8978 21.75 19.5V12H22.5C22.6989 12 22.8897 11.921 23.0303 11.7803C23.171 11.6397 23.25 11.4489 23.25 11.25C23.25 11.0511 23.171 10.8603 23.0303 10.7197C22.8897 10.579 22.6989 10.5 22.5 10.5ZM6.4875 5.25H17.5125L19.8459 10.5H4.15406L6.4875 5.25ZM6 19.5H3.75V18H6V19.5ZM18 19.5V18H20.25V19.5H18ZM20.25 16.5H3.75V12H20.25V16.5ZM5.25 14.25C5.25 14.0511 5.32902 13.8603 5.46967 13.7197C5.61032 13.579 5.80109 13.5 6 13.5H7.5C7.69891 13.5 7.88968 13.579 8.03033 13.7197C8.17098 13.8603 8.25 14.0511 8.25 14.25C8.25 14.4489 8.17098 14.6397 8.03033 14.7803C7.88968 14.921 7.69891 15 7.5 15H6C5.80109 15 5.61032 14.921 5.46967 14.7803C5.32902 14.6397 5.25 14.4489 5.25 14.25ZM15.75 14.25C15.75 14.0511 15.829 13.8603 15.9697 13.7197C16.1103 13.579 16.3011 13.5 16.5 13.5H18C18.1989 13.5 18.3897 13.579 18.5303 13.7197C18.671 13.8603 18.75 14.0511 18.75 14.25C18.75 14.4489 18.671 14.6397 18.5303 14.7803C18.3897 14.921 18.1989 15 18 15H16.5C16.3011 15 16.1103 14.921 15.9697 14.7803C15.829 14.6397 15.75 14.4489 15.75 14.25Z"
                fill="currentColor"
            />
        </svg>
    );

const ListIcon = ({ filled }: IconProps) =>
    filled ? (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M21 11.25V12.75C21 12.9489 20.921 13.1397 20.7803 13.2803C20.6397 13.421 20.4489 13.5 20.25 13.5H3.75C3.55109 13.5 3.36032 13.421 3.21967 13.2803C3.07902 13.1397 3 12.9489 3 12.75V11.25C3 11.0511 3.07902 10.8603 3.21967 10.7197C3.36032 10.579 3.55109 10.5 3.75 10.5H20.25C20.4489 10.5 20.6397 10.579 20.7803 10.7197C20.921 10.8603 21 11.0511 21 11.25ZM20.25 16.5H3.75C3.55109 16.5 3.36032 16.579 3.21967 16.7197C3.07902 16.8603 3 17.0511 3 17.25V18.75C3 18.9489 3.07902 19.1397 3.21967 19.2803C3.36032 19.421 3.55109 19.5 3.75 19.5H20.25C20.4489 19.5 20.6397 19.421 20.7803 19.2803C20.921 19.1397 21 18.9489 21 18.75V17.25C21 17.0511 20.921 16.8603 20.7803 16.7197C20.6397 16.579 20.4489 16.5 20.25 16.5ZM20.25 4.5H3.75C3.55109 4.5 3.36032 4.57902 3.21967 4.71967C3.07902 4.86032 3 5.05109 3 5.25V6.75C3 6.94891 3.07902 7.13968 3.21967 7.28033C3.36032 7.42098 3.55109 7.5 3.75 7.5H20.25C20.4489 7.5 20.6397 7.42098 20.7803 7.28033C20.921 7.13968 21 6.94891 21 6.75V5.25C21 5.05109 20.921 4.86032 20.7803 4.71967C20.6397 4.57902 20.4489 4.5 20.25 4.5Z"
                fill="currentColor"
            />
        </svg>
    ) : (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M21 12C21 12.1989 20.921 12.3897 20.7803 12.5303C20.6397 12.671 20.4489 12.75 20.25 12.75H3.75C3.55109 12.75 3.36032 12.671 3.21967 12.5303C3.07902 12.3897 3 12.1989 3 12C3 11.8011 3.07902 11.6103 3.21967 11.4697C3.36032 11.329 3.55109 11.25 3.75 11.25H20.25C20.4489 11.25 20.6397 11.329 20.7803 11.4697C20.921 11.6103 21 11.8011 21 12ZM3.75 6.75H20.25C20.4489 6.75 20.6397 6.67098 20.7803 6.53033C20.921 6.38968 21 6.19891 21 6C21 5.80109 20.921 5.61032 20.7803 5.46967C20.6397 5.32902 20.4489 5.25 20.25 5.25H3.75C3.55109 5.25 3.36032 5.32902 3.21967 5.46967C3.07902 5.61032 3 5.80109 3 6C3 6.19891 3.07902 6.38968 3.21967 6.53033C3.36032 6.67098 3.55109 6.75 3.75 6.75ZM20.25 17.25H3.75C3.55109 17.25 3.36032 17.329 3.21967 17.4697C3.07902 17.6103 3 17.8011 3 18C3 18.1989 3.07902 18.3897 3.21967 18.5303C3.36032 18.671 3.55109 18.75 3.75 18.75H20.25C20.4489 18.75 20.6397 18.671 20.7803 18.5303C20.921 18.3897 21 18.1989 21 18C21 17.8011 20.921 17.6103 20.7803 17.4697C20.6397 17.329 20.4489 17.25 20.25 17.25Z"
                fill="currentColor"
            />
        </svg>
    );

const ICON_BY_TAB: Record<PreviewTabKey, React.FC<IconProps>> = {
    home: HomeIcon,
    map: MapPinIcon,
    products: TicketIcon,
    carwash: CarIcon,
    'age-gated': TicketIcon,
    more: ListIcon,
};

const labelFor = (tab: PreviewTabKey, t: PreviewDictionary): string => {
    switch (tab) {
        case 'home':
            return t.bottomTabBar.home;
        case 'map':
            return t.bottomTabBar.map;
        case 'products':
            return t.bottomTabBar.products;
        case 'carwash':
            return t.bottomTabBar.carwash;
        case 'age-gated':
            return t.bottomTabBar.ageGated;
        case 'more':
            return t.bottomTabBar.more;
    }
};

type PreviewBottomTabBarProps = {
    selectedTab: PreviewTabKey;
    onTabChange: (tab: PreviewTabKey) => void;
    /**
     * In the Coupon and Featured edit pages the preview is rendered in
     * a read-only, "design-time" mode. The tab bar is then visible but
     * not interactive — clicks are swallowed and the inactive tabs are
     * dimmed.
     */
    readOnly?: boolean;
};

/**
 * Bottom tab bar of the simulated app. The list of visible tabs is read
 * from the active preview theme (`usePreviewTheme().tabs`), so QC shows
 * Carwash and CA shows the age-gated section without this component
 * knowing about BUs.
 *
 * The active tab — both icon and label — is colored with
 * `var(--preview-primary)` and uses the *filled* glyph variant; the
 * inactive tabs render the outline variant instead.
 */
export const PreviewBottomTabBar = ({
    selectedTab,
    onTabChange,
    readOnly = false,
}: PreviewBottomTabBarProps) => {
    const theme = usePreviewTheme();
    const t = useT();

    return (
        <nav className={style.navbar}>
            {theme.tabs.map((tab) => {
                const Icon = ICON_BY_TAB[tab];
                const label = labelFor(tab, t);
                const isActive = tab === selectedTab;
                const tabClass = [
                    style.tab,
                    isActive ? style.tab_active : '',
                    readOnly && !isActive ? style.tab_disabled : '',
                ]
                    .filter(Boolean)
                    .join(' ');
                return (
                    <button
                        key={tab}
                        type="button"
                        className={tabClass}
                        onClick={() => {
                            if (!readOnly) onTabChange(tab);
                        }}
                        aria-current={isActive ? 'page' : undefined}
                    >
                        <Icon filled={isActive} />
                        <span className={style.label}>{label}</span>
                    </button>
                );
            })}
        </nav>
    );
};
